import React, { useState, useMemo } from 'react';
import { useAppContext } from '../../context/AppContext';
import useFacilityFeatures from '../../hooks/useFacilityFeatures';
import { useToast } from '../Common/ToastProvider';
import { 
  Package, CreditCard, CheckCircle, AlertTriangle, 
  Clock, Users, FileText, BarChart3, TrendingUp, Download,
  Search, Filter, Plus, Edit, Eye, Trash2, User, Shield, Activity, 
  Settings, Pill, Target, RefreshCw, Send, MoreVertical, ChevronRight,
  X, Check, ChevronDown, ChevronUp, Minus
} from 'lucide-react';

const Orders = () => {
  const { state } = useAppContext();
  const { showToast } = useToast();
  
  // Get facility features for conditional UI
  const { isNDIS, isAgedCare } = useFacilityFeatures();
  
  // Helper function to get accommodation terminology
  const getAccommodationTerm = (wardName) => {
    if (isNDIS) {
      // For NDIS, convert "Ward X" to "Unit X" or "Room X"
      return wardName.replace(/Ward/gi, 'Unit');
    }
    // For Aged Care, keep "Ward"
    return wardName;
  };
  
  // Get accommodation options based on facility type
  const getAccommodationOptions = () => {
    if (isNDIS) {
      return ['Unit A', 'Unit B', 'Unit C', 'Unit D', 'Unit E', 'Room 101', 'Room 102', 'Room 103'];
    }
    // Aged Care uses Wards
    return ['Ward A', 'Ward B', 'Ward C', 'Ward D', 'Ward E'];
  };
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showBulkOrderModal, setShowBulkOrderModal] = useState(false);
  const [selectedResidents, setSelectedResidents] = useState([]);
  const [bulkOrderItems, setBulkOrderItems] = useState([]);
  const [refillOrders, setRefillOrders] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [bulkOrderStep, setBulkOrderStep] = useState(1); // 1: Select Patients, 2: Review Medications, 3: Group Orders
  const [editingMedication, setEditingMedication] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState(new Set()); // Track which orders are expanded
  const [allExpanded, setAllExpanded] = useState(true); // Track if all orders are expanded
  const [expandedMedications, setExpandedMedications] = useState(new Set()); // Track which resident groups have expanded medication lists

  // Generate comprehensive orders data for 50-100 patients (memoized to prevent regeneration)
  const ordersData = useMemo(() => {
    const facilities = ['Sunshine Gardens', 'Riverside Care', 'Mountain View Manor', 'Coastal Living', 'Valley Heights'];
    const accommodationOptions = getAccommodationOptions();
    const medications = [
      'Metformin 500mg', 'Donepezil 10mg', 'Amlodipine 5mg', 'Furosemide 40mg', 'Lisinopril 10mg',
      'Atorvastatin 20mg', 'Omeprazole 20mg', 'Paracetamol 500mg', 'Warfarin 5mg', 'Digoxin 0.25mg',
      'Insulin Glargine', 'Morphine 10mg', 'Tramadol 50mg', 'Codeine 30mg', 'Prednisolone 5mg',
      'Hydrocortisone 1%', 'Salbutamol Inhaler', 'Budesonide Inhaler', 'Fluoxetine 20mg', 'Sertraline 50mg'
    ];
    const doctors = ['Dr. Sarah Johnson', 'Dr. Michael Chen', 'Dr. David Kim', 'Dr. Lisa Wang', 'Dr. James Brown', 'Dr. Emma Wilson'];
    const statuses = ['Pending', 'Processing', 'Completed', 'Cancelled'];
    const priorities = ['High', 'Medium', 'Low'];
    
    // Real patient names for more realistic data
    const patientNames = [
      'Eleanor Mary Thompson', 'James Robert Wilson', 'Margaret Elizabeth Davis',
      'William Thomas Anderson', 'Dorothy Jean Smith', 'Robert Michael Johnson',
      'Helen Patricia Brown', 'Charles Edward Miller', 'Ruth Ann Garcia',
      'Frank Joseph Martinez', 'Betty Lou Rodriguez', 'George Henry Lee',
      'Shirley Mae White', 'Harold Raymond Harris', 'Mildred Rose Clark',
      'Raymond Albert Lewis', 'Frances Marie Walker', 'Arthur James Hall',
      'Evelyn Grace Allen', 'Donald Richard Young', 'Lois Jean King',
      'Kenneth Wayne Wright', 'Norma Jean Lopez', 'Gerald Francis Hill',
      'Joyce Ann Scott', 'Lawrence Edward Green', 'Phyllis Marie Adams',
      'Howard Michael Baker', 'Gloria Jean Nelson', 'Ralph Edward Carter',
      'Marilyn Ruth Mitchell', 'Eugene Joseph Perez', 'Janet Marie Roberts',
      'Wayne Douglas Turner', 'Carol Ann Phillips', 'Bruce Alan Campbell',
      'Diane Marie Parker', 'Roger Wayne Evans', 'Linda Jean Edwards',
      'Carl Raymond Collins', 'Donna Marie Stewart', 'Anthony Joseph Sanchez',
      'Barbara Ann Morris', 'Paul Douglas Rogers', 'Sandra Jean Reed',
      'Mark Alan Cook', 'Nancy Marie Morgan', 'Steven James Bell',
      'Susan Elizabeth Murphy', 'Kevin Michael Bailey', 'Karen Ann Rivera',
      'Brian David Cooper', 'Lisa Marie Richardson', 'Timothy James Cox',
      'Jennifer Lynn Ward', 'Christopher Michael Torres', 'Michelle Ann Peterson',
      'Daniel Robert Gray', 'Kimberly Jean Ramirez', 'Matthew James James',
      'Amy Elizabeth Watson', 'Andrew Michael Brooks', 'Angela Marie Kelly',
      'Joshua David Sanders', 'Brenda Jean Price', 'Ryan Michael Bennett',
      'Deborah Ann Wood', 'Jason David Barnes', 'Stephanie Marie Ross',
      'Justin Michael Henderson', 'Cynthia Jean Coleman', 'Brandon David Jenkins',
      'Pamela Marie Perry', 'Tyler James Powell', 'Heather Ann Long',
      'Zachary Michael Patterson', 'Samantha Jean Hughes', 'Nicholas David Flores',
      'Rebecca Marie Washington', 'Aaron James Butler', 'Amanda Jean Simmons',
      'Kyle David Foster', 'Nicole Marie Gonzales', 'Jacob Michael Bryant'
    ];
    
    const orders = [];
    let orderId = 1001;
    let batchOrderId = 1;
    
    // Generate orders with multiple residents and medications
    for (let orderIndex = 0; orderIndex < 25; orderIndex++) {
      const facility = facilities[Math.floor(Math.random() * facilities.length)];
      const accommodation = accommodationOptions[Math.floor(Math.random() * accommodationOptions.length)];
      const currentOrderId = `Order ${batchOrderId++}`;
      const orderPriority = priorities[Math.floor(Math.random() * priorities.length)];
      const orderCreatedAt = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString();
      const orderDelivery = new Date(Date.now() + Math.random() * 3 * 24 * 60 * 60 * 1000).toISOString();
      
      // Each order has 15-20 residents (realistic batch size)
      const residentCount = Math.floor(Math.random() * 6) + 15; // 15-20 residents
      const selectedResidents = [];
      
      // Select random residents for this order (ensure unique)
      while (selectedResidents.length < residentCount) {
        const randomPatientId = Math.floor(Math.random() * 75) + 1;
        if (!selectedResidents.includes(randomPatientId)) {
          selectedResidents.push(randomPatientId);
        }
      }
      
      // Generate medications for each resident in this order
      // For some orders, mix cancelled and active statuses within the same batch
      const shouldMixStatuses = orderIndex < 5; // First 5 orders will have mixed statuses
      let medicationIndexInBatch = 0; // Track medication index within the batch
      
      selectedResidents.forEach(patientId => {
        const patientName = patientNames[patientId - 1] || `Patient ${patientId.toString().padStart(3, '0')}`;
        const medicationCount = Math.floor(Math.random() * 2) + 3; // 3-4 medications per resident
        
        for (let medIndex = 0; medIndex < medicationCount; medIndex++) {
          const medication = medications[Math.floor(Math.random() * medications.length)];
          const doctor = doctors[Math.floor(Math.random() * doctors.length)];
          
          // For mixed status orders, randomly assign status to each medication
          // Otherwise, use the same status for all items in the batch
          let itemStatus;
          if (shouldMixStatuses) {
            // Mix of cancelled and active statuses
            // Ensure first order has at least one cancelled and one active
            if (orderIndex === 0) {
              // First order: alternate or ensure mix
              if (medicationIndexInBatch % 2 === 0) {
                itemStatus = ['Pending', 'Processing', 'Completed'][Math.floor(Math.random() * 3)];
              } else {
                itemStatus = 'Cancelled';
              }
            } else {
              // Other mixed orders: random mix
              const mixedStatuses = ['Pending', 'Processing', 'Completed', 'Cancelled'];
              itemStatus = mixedStatuses[Math.floor(Math.random() * mixedStatuses.length)];
            }
          } else {
            // All items in batch have same status
            itemStatus = statuses[Math.floor(Math.random() * statuses.length)];
          }
          
          medicationIndexInBatch++;
          
          orders.push({
            id: `ORD-${orderId++}`,
            batchOrderId: currentOrderId,
            facility,
            accommodation,
            residentId: patientId,
            residentName: patientName,
            medication,
            dosage: `${Math.floor(Math.random() * 50) + 10}mg ${['once daily', 'twice daily', 'three times daily'][Math.floor(Math.random() * 3)]}`,
            quantity: Math.floor(Math.random() * 60) + 30,
            prescribedBy: doctor,
            status: itemStatus,
            priority: orderPriority,
            createdAt: orderCreatedAt,
            estimatedDelivery: orderDelivery,
            totalCost: itemStatus === 'Cancelled' ? 0 : Math.round((Math.random() * 50 + 10) * 100) / 100,
            pbsEligible: Math.random() > 0.2,
            authority: Math.random() > 0.8,
            notes: `${patientName} medication order`,
            bulkOrder: false
          });
        }
      });
    }
    
    return orders;
  }, [isNDIS, isAgedCare]); // Regenerate when facility type changes

  // Comprehensive patient data with medications
  const patientMedicationData = [
    {
      id: 1,
      name: 'Eleanor Mary Thompson',
      accommodation: isNDIS ? 'Unit A' : 'Ward A',
      room: 'A-12',
      age: 83,
      medications: [
        {
          id: 'MED-001',
          name: 'Metformin 500mg',
          dosage: '500mg twice daily',
          frequency: 'BID',
          prescribedBy: 'Dr. Sarah Johnson',
          startDate: '2023-03-20',
          lastRefill: '2024-01-15',
          nextRefill: '2024-02-15',
          repeatsRemaining: 2,
          totalRepeats: 5,
          pbsEligible: true,
          authority: false,
          unitPrice: 12.50,
          category: 'Antidiabetic',
          instructions: 'Take with food to reduce stomach upset',
          sideEffects: 'Nausea, diarrhea, metallic taste',
          monitoring: 'Blood glucose levels, kidney function',
          status: 'Active'
        },
        {
          id: 'MED-002',
          name: 'Donepezil 10mg',
          dosage: '10mg once daily',
          frequency: 'OD',
          prescribedBy: 'Dr. Michael Chen',
          startDate: '2023-04-01',
          lastRefill: '2024-01-10',
          nextRefill: '2024-02-10',
          repeatsRemaining: 1,
          totalRepeats: 3,
          pbsEligible: true,
          authority: false,
          unitPrice: 8.75,
          category: 'Anticholinesterase',
          instructions: 'Take at bedtime',
          sideEffects: 'Nausea, vomiting, diarrhea',
          monitoring: 'Cognitive function, liver function',
          status: 'Active'
        }
      ]
    },
    {
      id: 2,
      name: 'Robert James Wilson',
      accommodation: isNDIS ? 'Unit B' : 'Ward B',
      room: 'B-08',
      age: 76,
      medications: [
        {
          id: 'MED-003',
          name: 'Amlodipine 5mg',
          dosage: '5mg once daily',
          frequency: 'OD',
          prescribedBy: 'Dr. David Kim',
          startDate: '2023-08-25',
          lastRefill: '2024-01-12',
          nextRefill: '2024-02-12',
          repeatsRemaining: 3,
          totalRepeats: 5,
          pbsEligible: true,
          authority: false,
          unitPrice: 9.25,
          category: 'Calcium Channel Blocker',
          instructions: 'Take at the same time each day',
          sideEffects: 'Swelling of ankles, dizziness',
          monitoring: 'Blood pressure, kidney function',
          status: 'Active'
        }
      ]
    },
    {
      id: 3,
      name: 'Margaret Rose Davis',
      accommodation: isNDIS ? 'Unit C' : 'Ward C',
      room: 'C-15',
      age: 89,
      medications: [
        {
          id: 'MED-004',
          name: 'Furosemide 40mg',
          dosage: '40mg once daily',
          frequency: 'OD',
          prescribedBy: 'Dr. Lisa Wang',
          startDate: '2023-11-15',
          lastRefill: '2024-01-08',
          nextRefill: '2024-02-08',
          repeatsRemaining: 4,
          totalRepeats: 5,
          pbsEligible: true,
          authority: false,
          unitPrice: 6.50,
          category: 'Diuretic',
          instructions: 'Take in the morning',
          sideEffects: 'Increased urination, dehydration risk',
          monitoring: 'Kidney function, electrolyte levels',
          status: 'Active'
        }
      ]
    }
  ];

  // Sample medications for bulk order
  const availableMedications = [
    { name: 'Metformin 500mg', unitPrice: 12.50, pbsEligible: true },
    { name: 'Donepezil 10mg', unitPrice: 8.75, pbsEligible: true },
    { name: 'Amlodipine 5mg', unitPrice: 9.25, pbsEligible: true },
    { name: 'Furosemide 40mg', unitPrice: 6.50, pbsEligible: true },
    { name: 'Lisinopril 10mg', unitPrice: 7.50, pbsEligible: true },
    { name: 'Atorvastatin 20mg', unitPrice: 15.25, pbsEligible: true },
    { name: 'Insulin Glargine 100units/ml', unitPrice: 45.60, pbsEligible: true }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'text-green-600 bg-green-100';
      case 'Processing': return 'text-blue-600 bg-blue-100';
      case 'Pending': return 'text-yellow-600 bg-yellow-100';
      case 'Cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'text-red-600 bg-red-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityHeaderColor = (priority) => {
    switch (priority) {
      case 'High': return 'from-red-600 to-red-700 border-red-500';
      case 'Medium': return 'from-yellow-600 to-yellow-700 border-yellow-500';
      case 'Low': return 'from-green-600 to-green-700 border-green-500';
      default: return 'from-indigo-600 to-purple-600 border-indigo-500';
    }
  };

  const getPriorityBorderColor = (priority) => {
    switch (priority) {
      case 'High': return 'border-l-4 border-red-500';
      case 'Medium': return 'border-l-4 border-yellow-500';
      case 'Low': return 'border-l-4 border-green-500';
      default: return 'border-l-4 border-gray-400';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredOrders = ordersData.filter(order => {
    const matchesSearch = order.medication.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         order.residentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         order.facility.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || order.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const handleBulkOrder = () => {
    setShowBulkOrderModal(true);
    setBulkOrderStep(1);
    setSelectedPatients([]);
    setSelectedMedications([]);
  };

  const selectAllPatients = () => {
    setSelectedPatients(patientMedicationData);
  };

  const togglePatientSelection = (patient) => {
    setSelectedPatients(prev => 
      prev.some(p => p.id === patient.id)
        ? prev.filter(p => p.id !== patient.id)
        : [...prev, patient]
    );
  };

  const toggleMedicationSelection = (medication) => {
    setSelectedMedications(prev => 
      prev.some(m => m.name === medication.name)
        ? prev.filter(m => m.name !== medication.name)
        : [...prev, medication]
    );
  };

  const updateRepeats = (medication, newRepeats) => {
    // In a real app, this would update the backend
    showToast(`Repeats updated for ${medication.name}: ${newRepeats} repeats remaining`, 'success');
    setEditingMedication(null);
  };

  const tabs = [
    { id: 'overview', label: '📊 Overview', icon: BarChart3 },
    { id: 'orders', label: '📦 Orders', icon: Package },
    { id: 'refills', label: '🔄 Refills', icon: RefreshCw },
    { id: 'processing', label: '⚙️ Processing', icon: Settings },
    { id: 'compliance', label: '🛡️ Compliance', icon: Shield },
    { id: 'reports', label: '📈 Reports', icon: FileText }
  ];

  return (
    <div className="space-y-6">
      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl p-6 text-white shadow-xl min-h-[120px] flex items-center">
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <Package className="w-8 h-8 text-white" />
            </div>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
                <Package className="w-8 h-8" />
              {isNDIS ? 'Service Orders' : 'Orders Management'}
            </h1>
              <p className="text-blue-100 text-base mt-2">
                {isNDIS 
                  ? 'NDIS service orders and medication management' 
                  : 'Comprehensive medication ordering and management system'}
              </p>
          </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{ordersData.length}</div>
            <div className="text-blue-100 text-sm">Active Orders</div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
        <div className="flex space-x-1 p-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
            </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200 shadow-lg h-[140px] flex flex-col justify-between">
                <div className="flex items-center justify-between">
            <div>
                    <p className="text-blue-600 text-sm font-medium">Total Orders</p>
                    <p className="text-3xl font-bold text-blue-900">{ordersData.length}</p>
              </div>
                  <Package className="w-8 h-8 text-blue-600" />
            </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200 shadow-lg h-[140px] flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-600 text-sm font-medium">Completed</p>
                    <p className="text-3xl font-bold text-green-900">
                      {ordersData.filter(o => o.status === 'Completed').length}
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
        
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-6 border border-yellow-200 shadow-lg h-[140px] flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-yellow-600 text-sm font-medium">Pending</p>
                    <p className="text-3xl font-bold text-yellow-900">
                      {ordersData.filter(o => o.status === 'Pending').length}
                    </p>
            </div>
                  <Clock className="w-8 h-8 text-yellow-600" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200 shadow-lg h-[140px] flex flex-col justify-between">
                <div className="flex items-center justify-between">
            <div>
                    <p className="text-purple-600 text-sm font-medium">Total Value</p>
                    <p className="text-3xl font-bold text-purple-900">
                      {formatCurrency(ordersData.reduce((sum, order) => sum + (order.status === 'Cancelled' ? 0 : order.totalCost), 0))}
                    </p>
              </div>
                  <CreditCard className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>
        
            {/* Recent Orders */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Recent Orders</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {ordersData.slice(0, 5).map((order, index) => (
                    <div
                      key={order.id}
                      className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-300"
                      style={{
                        animationDelay: `${index * 100}ms`,
                        animation: 'fadeInUp 0.6s ease-out forwards'
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                            <Package className="w-6 h-6 text-white" />
            </div>
            <div>
                            <h4 className="font-bold text-gray-900">{order.medication}</h4>
                            <p className="text-sm text-gray-600">{order.residentName} • {getAccommodationTerm(order.accommodation || order.ward)}</p>
                            <p className="text-xs text-gray-500">Prescribed by {order.prescribedBy}</p>
              </div>
            </div>
                        <div className="text-right">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                              {order.status}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(order.priority)}`}>
                              {order.priority}
                            </span>
          </div>
                          <p className="text-sm font-bold text-gray-900">{formatCurrency(order.status === 'Cancelled' ? 0 : order.totalCost)}</p>
                          <p className="text-xs text-gray-500">{formatDate(order.createdAt)}</p>
        </div>
            </div>
              </div>
                  ))}
            </div>
          </div>
        </div>
      </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
          {/* Search and Filters */}
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200 shadow-sm">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                    placeholder="Search orders by medication, patient, or facility..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white shadow-sm"
              />
            </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white shadow-sm font-medium"
              >
                <option value="all">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Processing">Processing</option>
                <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                  className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white shadow-sm font-medium"
              >
                <option value="all">All Priority</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
                {/* Only show Bulk Order button for Aged Care or NDIS */}
                {(isAgedCare || isNDIS) && (
                  <button
                    onClick={handleBulkOrder}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Bulk Order
                  </button>
                )}
            </div>
          </div>

            {/* Grouped Orders by Order Number */}
            <div className="space-y-6">
              {/* Master Expand/Collapse All Button */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => {
                    if (allExpanded) {
                      // Collapse all
                      setExpandedOrders(new Set());
                      setAllExpanded(false);
                    } else {
                      // Expand all
                      const allOrderIds = new Set(
                        Object.values(filteredOrders.reduce((acc, order) => {
                          acc[order.batchOrderId] = true;
                          return acc;
                        }, {})).map((_, idx, arr) => Object.keys(arr)[idx])
                      );
                      const orderIds = Array.from(
                        new Set(filteredOrders.map(o => o.batchOrderId))
                      );
                      setExpandedOrders(new Set(orderIds));
                      setAllExpanded(true);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
                >
                  {allExpanded ? (
                    <>
                      <Minus className="w-4 h-4" />
                      Collapse All
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Expand All
                    </>
                  )}
                </button>
              </div>

              {(() => {
                // Group orders by batch order ID first, then by resident
                const groupedOrders = filteredOrders.reduce((acc, order) => {
                  if (!acc[order.batchOrderId]) {
                    acc[order.batchOrderId] = {
                      batchOrderId: order.batchOrderId,
                      status: order.status,
                      priority: order.priority,
                      createdAt: order.createdAt,
                      estimatedDelivery: order.estimatedDelivery,
                      facility: order.facility,
                      accommodation: order.accommodation || order.ward,
                      residents: {}
                    };
                  }
                  
                  // Group by resident within each batch order
                  if (!acc[order.batchOrderId].residents[order.residentId]) {
                    acc[order.batchOrderId].residents[order.residentId] = {
                      resident: {
                        id: order.residentId,
                        name: order.residentName,
                        accommodation: order.accommodation || order.ward,
                        facility: order.facility
                      },
                      medications: []
                    };
                  }
                  
                  acc[order.batchOrderId].residents[order.residentId].medications.push(order);
                  return acc;
                }, {});

                return Object.values(groupedOrders).map((batchOrder, batchIndex) => {
                  const totalMedications = Object.values(batchOrder.residents).reduce((sum, resident) => sum + resident.medications.length, 0);
                  const totalCost = Object.values(batchOrder.residents).reduce((sum, resident) => 
                    sum + resident.medications.reduce((medSum, med) => medSum + (med.status === 'Cancelled' ? 0 : med.totalCost), 0), 0
                  );
                  const totalPatients = Object.keys(batchOrder.residents).length;
                  
                  const isExpanded = expandedOrders.has(batchOrder.batchOrderId) || allExpanded;
                  
                  const toggleOrder = () => {
                    const newExpanded = new Set(expandedOrders);
                    if (isExpanded) {
                      newExpanded.delete(batchOrder.batchOrderId);
                    } else {
                      newExpanded.add(batchOrder.batchOrderId);
                    }
                    setExpandedOrders(newExpanded);
                    // Update allExpanded state if needed
                    const allOrderIds = new Set(Object.values(groupedOrders).map(o => o.batchOrderId));
                    setAllExpanded(newExpanded.size === allOrderIds.size);
                  };
                  
                  return (
                    <div 
                      key={batchOrder.batchOrderId}
                      className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden"
                      style={{
                        animationDelay: `${batchIndex * 100}ms`,
                        animation: 'fadeInUp 0.6s ease-out forwards'
                      }}
                    >
                      {/* Batch Order Header */}
                      <div className={`bg-gradient-to-r ${getPriorityHeaderColor(batchOrder.priority)} text-white p-4 border-l-4`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <button
                              onClick={toggleOrder}
                              className="flex items-center justify-center w-8 h-8 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors cursor-pointer"
                              title={isExpanded ? "Collapse" : "Expand"}
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-white" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-white" />
                              )}
                            </button>
                            <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-white font-bold text-lg">
                              {batchOrder.batchOrderId.split(' ')[1]}
                            </div>
                            <div>
                              <h3 className="text-xl font-bold">{batchOrder.batchOrderId}</h3>
                              <p className="text-indigo-100 text-sm">{batchOrder.facility} • {getAccommodationTerm(batchOrder.accommodation || batchOrder.ward)}</p>
                              <p className="text-indigo-200 text-xs">Created: {new Date(batchOrder.createdAt).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-6">
                              <div className="text-center w-20">
                                <div className="text-sm text-indigo-100 mb-1">Status</div>
                                <div className="text-lg font-bold">{batchOrder.status}</div>
                              </div>
                              <div className="text-center w-20">
                                <div className="text-sm text-indigo-100 mb-1">Patients</div>
                                <div className="text-lg font-bold">{totalPatients}</div>
                              </div>
                              <div className="text-center w-20">
                                <div className="text-sm text-indigo-100 mb-1">Priority</div>
                                <div className="text-lg font-bold">{batchOrder.priority}</div>
                              </div>
                              <div className="text-center w-24">
                                <div className="text-sm text-indigo-100 mb-1">Total Meds</div>
                                <div className="text-xl font-bold">{totalMedications}</div>
                              </div>
                              <div className="text-center w-28">
                                <div className="text-sm text-indigo-100 mb-1">Total Cost</div>
                                <div className="text-lg font-bold text-yellow-300">{formatCurrency(totalCost)}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Residents and Medications - Collapsible */}
                      {isExpanded && (
                        <div className="p-4">
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {Object.values(batchOrder.residents).map((residentGroup, residentIndex) => (
                              <div key={residentGroup.resident.id} className="bg-white rounded-lg p-3 border border-gray-200">
                                {/* Resident Header */}
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs">
                                    {residentGroup.resident.name.split(' ').map(n => n[0]).join('')}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-gray-900 text-sm truncate">{residentGroup.resident.name}</h4>
                                    <p className="text-xs text-gray-600 truncate">{residentGroup.resident.facility} • {getAccommodationTerm(residentGroup.resident.accommodation || residentGroup.resident.ward)}</p>
                                  </div>
                                </div>

                                {/* Resident Summary */}
                                <div className="flex justify-between items-center mb-2 text-xs">
                                  <span className="text-gray-600">Medications: {residentGroup.medications.length}</span>
                                  <span className="font-semibold text-gray-900">
                                    {formatCurrency(residentGroup.medications.reduce((sum, med) => sum + (med.status === 'Cancelled' ? 0 : med.totalCost), 0))}
                                  </span>
                                </div>

                                {/* Medications List - Expandable */}
                                <div className="space-y-1">
                                  {(() => {
                                    const medicationKey = `${batchOrder.batchOrderId}-${residentGroup.resident.id}`;
                                    const isMedicationsExpanded = expandedMedications.has(medicationKey);
                                    const medicationsToShow = isMedicationsExpanded 
                                      ? residentGroup.medications 
                                      : residentGroup.medications.slice(0, 4);
                                    
                                    return (
                                      <>
                                        {medicationsToShow.map((medication, medIndex) => (
                                          <div key={medication.id} className={`bg-gray-50 rounded p-2 text-xs ${getPriorityBorderColor(medication.priority)}`}>
                                            <div className="flex items-center justify-between mb-1">
                                              <span className="font-medium text-gray-900 truncate pr-1">{medication.medication}</span>
                                              <span className={`px-1 py-0.5 rounded text-xs font-medium ${getStatusColor(medication.status)}`}>
                                                {medication.status}
                                              </span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                              <span className="text-gray-600">{medication.dosage}</span>
                                              <span className="font-semibold text-green-600">{formatCurrency(medication.status === 'Cancelled' ? 0 : medication.totalCost)}</span>
                                            </div>
                                          </div>
                                        ))}
                                        {residentGroup.medications.length > 4 && (
                                          <button
                                            onClick={() => {
                                              const newExpanded = new Set(expandedMedications);
                                              if (isMedicationsExpanded) {
                                                newExpanded.delete(medicationKey);
                                              } else {
                                                newExpanded.add(medicationKey);
                                              }
                                              setExpandedMedications(newExpanded);
                                            }}
                                            className="w-full text-xs text-blue-600 hover:text-blue-800 font-medium text-center py-2 hover:bg-blue-50 rounded transition-colors flex items-center justify-center gap-1"
                                          >
                                            {isMedicationsExpanded ? (
                                              <>
                                                <ChevronUp className="w-3 h-3" />
                                                Show Less
                                              </>
                                            ) : (
                                              <>
                                                <ChevronDown className="w-3 h-3" />
                                                +{residentGroup.medications.length - 4} more medications
                                              </>
                                            )}
                                          </button>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {/* Refills Tab */}
        {activeTab === 'refills' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-6 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold mb-2">Medication Refills Management</h2>
              <p className="opacity-90">Track and manage medication refills and renewals</p>
            </div>

            {/* Refill Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-6 border border-red-200 shadow-lg h-[140px] flex flex-col justify-between">
                <div className="flex items-center justify-between">
                        <div>
                    <p className="text-red-600 text-sm font-medium">Due Soon</p>
                    <p className="text-3xl font-bold text-red-900">
                      {ordersData.filter(o => o.status === 'Pending' && new Date(o.estimatedDelivery) <= new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)).length}
                    </p>
                        </div>
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-6 border border-yellow-200 shadow-lg h-[140px] flex flex-col justify-between">
                <div className="flex items-center justify-between">
                        <div>
                    <p className="text-yellow-600 text-sm font-medium">Upcoming</p>
                    <p className="text-3xl font-bold text-yellow-900">
                      {ordersData.filter(o => o.status === 'Processing').length}
                    </p>
                        </div>
                  <Clock className="w-8 h-8 text-yellow-600" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200 shadow-lg h-[140px] flex flex-col justify-between">
                <div className="flex items-center justify-between">
                        <div>
                    <p className="text-green-600 text-sm font-medium">Auto-Refill ON</p>
                    <p className="text-3xl font-bold text-green-900">
                      {Math.floor(ordersData.length * 0.6)}
                    </p>
                        </div>
                  <RefreshCw className="w-8 h-8 text-green-600" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200 shadow-lg h-[140px] flex flex-col justify-between">
                <div className="flex items-center justify-between">
                        <div>
                    <p className="text-blue-600 text-sm font-medium">Total Refills</p>
                    <p className="text-3xl font-bold text-blue-900">{ordersData.length}</p>
                  </div>
                  <Package className="w-8 h-8 text-blue-600" />
                </div>
                        </div>
                      </div>

            {/* Upcoming Refills */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Upcoming Refills</h3>
              </div>
              <div className="p-6">
                {(() => {
                  // Group orders by patient/resident
                  const upcomingRefills = ordersData.filter(o => o.status === 'Pending' || o.status === 'Processing');
                  const groupedByPatient = upcomingRefills.reduce((acc, order) => {
                    const patientKey = `${order.residentId}-${order.residentName}`;
                    if (!acc[patientKey]) {
                      acc[patientKey] = {
                        residentId: order.residentId,
                        residentName: order.residentName,
                        facility: order.facility,
                        accommodation: order.accommodation || order.ward,
                        orders: []
                      };
                    }
                    acc[patientKey].orders.push(order);
                    return acc;
                  }, {});

                  const patientGroups = Object.values(groupedByPatient).slice(0, 10);

                  if (patientGroups.length === 0) {
                    return (
                      <div className="text-center py-12 text-gray-500">
                        <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p>No upcoming refills</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-6">
                      {patientGroups.map((patientGroup, groupIndex) => {
                        const totalCost = patientGroup.orders.reduce((sum, order) => 
                          sum + (order.status === 'Cancelled' ? 0 : order.totalCost), 0
                        );
                        
                        return (
                          <div
                            key={patientGroup.residentId}
                            className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-200 hover:shadow-md transition-all duration-300 overflow-hidden"
                            style={{
                              animationDelay: `${groupIndex * 100}ms`,
                              animation: 'fadeInUp 0.6s ease-out forwards'
                            }}
                          >
                            {/* Patient Header */}
                            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                                    <User className="w-6 h-6 text-white" />
                                  </div>
                                  <div>
                                    <h4 className="font-bold text-lg">{patientGroup.residentName}</h4>
                                    <p className="text-sm text-blue-100">{patientGroup.facility} • {getAccommodationTerm(patientGroup.accommodation || patientGroup.ward)}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-blue-100">Total Medications</p>
                                  <p className="text-xl font-bold">{patientGroup.orders.length}</p>
                                  <p className="text-xs text-blue-100 mt-1">Total: {formatCurrency(totalCost)}</p>
                                </div>
                              </div>
                            </div>

                            {/* Medications List */}
                            <div className="p-4 space-y-3">
                              {patientGroup.orders.map((order, orderIndex) => (
                                <div
                                  key={order.id}
                                  className="bg-white rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-colors"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4 flex-1">
                                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                                        <Pill className="w-5 h-5 text-white" />
                                      </div>
                                      <div className="flex-1">
                                        <h5 className="font-semibold text-gray-900">{order.medication}</h5>
                                        <div className="flex items-center gap-3 mt-1">
                                          <p className="text-sm text-gray-600">{order.dosage}</p>
                                          <span className="text-gray-400">•</span>
                                          <p className="text-xs text-gray-500">Qty: {order.quantity}</p>
                                          <span className="text-gray-400">•</span>
                                          <p className="text-xs text-gray-500">Due: {formatDate(order.estimatedDelivery)}</p>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">Prescribed by {order.prescribedBy}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3 ml-4">
                                      <div className="text-right">
                                        <p className="text-sm font-bold text-gray-900">
                                          {formatCurrency(order.status === 'Cancelled' ? 0 : order.totalCost)}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1">
                                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                                            {order.status}
                                          </span>
                                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(order.priority)}`}>
                                            {order.priority}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex flex-col gap-2">
                                        <button className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs font-medium whitespace-nowrap">
                                          Order Now
                                        </button>
                                        <button className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium whitespace-nowrap">
                                          Schedule
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Patient Actions */}
                            <div className="px-4 pb-4 pt-2 border-t border-gray-200 bg-gray-50">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Package className="w-4 h-4" />
                                  <span>{patientGroup.orders.length} medication{patientGroup.orders.length !== 1 ? 's' : ''} requiring refill</span>
                                </div>
                                <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium flex items-center gap-2">
                                  <RefreshCw className="w-4 h-4" />
                                  Order All
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Processing Tab */}
        {activeTab === 'processing' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold mb-2">Order Processing Workflow</h2>
              <p className="opacity-90">Track orders through the processing pipeline</p>
            </div>

            {/* Processing Pipeline */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-yellow-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Pending Review</h3>
                  <div className="text-3xl font-bold text-yellow-600 mb-2">
                    {ordersData.filter(o => o.status === 'Pending').length}
                  </div>
                  <p className="text-sm text-gray-600">Awaiting approval</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Settings className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">In Processing</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {ordersData.filter(o => o.status === 'Processing').length}
                  </div>
                  <p className="text-sm text-gray-600">Being prepared</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Completed</h3>
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {ordersData.filter(o => o.status === 'Completed').length}
                  </div>
                  <p className="text-sm text-gray-600">Ready for delivery</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Issues</h3>
                  <div className="text-3xl font-bold text-red-600 mb-2">
                    {ordersData.filter(o => o.status === 'Cancelled').length}
                  </div>
                  <p className="text-sm text-gray-600">Require attention</p>
                </div>
              </div>
            </div>

            {/* Processing Queue - Grouped by Order > Patient > Medication */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Processing Queue</h3>
                    <p className="text-sm text-gray-600 mt-1">Organized by Order, Patient, and Medication</p>
                  </div>
                  <button
                    onClick={() => {
                      const processingOrders = ordersData.filter(o => o.status === 'Processing' || o.status === 'Pending');
                      const orderIds = Array.from(new Set(processingOrders.map(o => o.batchOrderId)));
                      
                      if (allExpanded) {
                        setExpandedOrders(new Set());
                        setAllExpanded(false);
                      } else {
                        setExpandedOrders(new Set(orderIds));
                        setAllExpanded(true);
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
                  >
                    {allExpanded ? (
                      <>
                        <Minus className="w-4 h-4" />
                        Collapse All
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Expand All
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div className="p-6">
                {(() => {
                  // Filter processing orders
                  const processingOrders = ordersData.filter(o => o.status === 'Processing' || o.status === 'Pending');
                  
                  // Group by batchOrderId (Order)
                  const groupedByOrder = processingOrders.reduce((acc, order) => {
                    if (!acc[order.batchOrderId]) {
                      acc[order.batchOrderId] = {
                        batchOrderId: order.batchOrderId,
                        facility: order.facility,
                        accommodation: order.accommodation || order.ward,
                        status: order.status,
                        priority: order.priority,
                        createdAt: order.createdAt,
                        estimatedDelivery: order.estimatedDelivery,
                        patients: {}
                      };
                    }
                    
                    // Group by patient within each order
                    const patientKey = `${order.residentId}-${order.residentName}`;
                    if (!acc[order.batchOrderId].patients[patientKey]) {
                      acc[order.batchOrderId].patients[patientKey] = {
                        residentId: order.residentId,
                        residentName: order.residentName,
                        medications: []
                      };
                    }
                    
                    // Add medication to patient
                    acc[order.batchOrderId].patients[patientKey].medications.push(order);
                    return acc;
                  }, {});

                  const orderGroups = Object.values(groupedByOrder);

                  if (orderGroups.length === 0) {
                    return (
                      <div className="text-center py-12 text-gray-500">
                        <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p>No orders in processing</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-6">
                      {orderGroups.map((orderGroup, orderIndex) => {
                        const totalMedications = Object.values(orderGroup.patients).reduce(
                          (sum, patient) => sum + patient.medications.length, 0
                        );
                        const totalCost = Object.values(orderGroup.patients).reduce((sum, patient) =>
                          sum + patient.medications.reduce((medSum, med) => 
                            medSum + (med.status === 'Cancelled' ? 0 : med.totalCost), 0
                          ), 0
                        );
                        const totalPatients = Object.keys(orderGroup.patients).length;

                        const isExpanded = expandedOrders.has(orderGroup.batchOrderId) || allExpanded;
                        
                        const toggleOrder = () => {
                          const newExpanded = new Set(expandedOrders);
                          if (isExpanded) {
                            newExpanded.delete(orderGroup.batchOrderId);
                          } else {
                            newExpanded.add(orderGroup.batchOrderId);
                          }
                          setExpandedOrders(newExpanded);
                          const allOrderIds = new Set(orderGroups.map(o => o.batchOrderId));
                          setAllExpanded(newExpanded.size === allOrderIds.size);
                        };

                        return (
                          <div
                            key={orderGroup.batchOrderId}
                            className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300 overflow-hidden"
                            style={{
                              animationDelay: `${orderIndex * 100}ms`,
                              animation: 'fadeInUp 0.6s ease-out forwards'
                            }}
                          >
                            {/* Order Header */}
                            <div className={`bg-gradient-to-r ${getPriorityHeaderColor(orderGroup.priority)} text-white p-4 border-l-4`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4 flex-1">
                                  <button
                                    onClick={toggleOrder}
                                    className="flex items-center justify-center w-8 h-8 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors cursor-pointer"
                                    title={isExpanded ? "Collapse" : "Expand"}
                                  >
                                    {isExpanded ? (
                                      <ChevronDown className="w-5 h-5 text-white" />
                                    ) : (
                                      <ChevronRight className="w-5 h-5 text-white" />
                                    )}
                                  </button>
                                  <div className="w-14 h-14 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                                    <Package className="w-7 h-7 text-white" />
                                  </div>
                                  <div>
                                    <h3 className="text-xl font-bold">{orderGroup.batchOrderId}</h3>
                                    <p className="text-sm text-indigo-100">{orderGroup.facility} • {getAccommodationTerm(orderGroup.accommodation || orderGroup.ward)}</p>
                                    <p className="text-xs text-indigo-200 mt-1">
                                      Created: {formatDate(orderGroup.createdAt)} • 
                                      Expected: {formatDate(orderGroup.estimatedDelivery)}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="flex items-center gap-6">
                                    <div className="text-center w-20">
                                      <p className="text-xs text-indigo-100 mb-1">Status</p>
                                      <p className="text-lg font-bold">{orderGroup.status}</p>
                                    </div>
                                    <div className="text-center w-20">
                                      <p className="text-xs text-indigo-100 mb-1">Patients</p>
                                      <p className="text-lg font-bold">{totalPatients}</p>
                                    </div>
                                    <div className="text-center w-20">
                                      <p className="text-xs text-indigo-100 mb-1">Priority</p>
                                      <p className="text-lg font-bold">{orderGroup.priority}</p>
                                    </div>
                                    <div className="text-center w-24">
                                      <p className="text-xs text-indigo-100 mb-1">Total Items</p>
                                      <p className="text-2xl font-bold">{totalMedications}</p>
                                    </div>
                                    <div className="text-center w-28">
                                      <p className="text-xs text-indigo-100 mb-1">Total</p>
                                      <p className="text-lg font-bold text-yellow-300">{formatCurrency(totalCost)}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Patients within Order - Collapsible */}
                            {isExpanded && (
                              <div className="p-4 space-y-4">
                                {Object.values(orderGroup.patients).map((patient, patientIndex) => {
                                const patientTotalCost = patient.medications.reduce((sum, med) =>
                                  sum + (med.status === 'Cancelled' ? 0 : med.totalCost), 0
                                );

                                return (
                                  <div
                                    key={patient.residentId}
                                    className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                                  >
                                    {/* Patient Header */}
                                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-3 border-b border-gray-200">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                                            <User className="w-5 h-5 text-white" />
                                          </div>
                                          <div>
                                            <h4 className="font-semibold text-gray-900">{patient.residentName}</h4>
                                            <p className="text-xs text-gray-600">Patient ID: {patient.residentId}</p>
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-xs text-gray-600">Medications</p>
                                          <p className="text-lg font-bold text-gray-900">{patient.medications.length}</p>
                                          <p className="text-xs text-gray-600">Total: {formatCurrency(patientTotalCost)}</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Medications List */}
                                    <div className="p-3 space-y-2">
                                      {patient.medications.map((medication, medIndex) => (
                                        <div
                                          key={medication.id}
                                          className={`bg-gray-50 rounded-lg p-3 border border-gray-100 hover:border-blue-300 transition-colors ${getPriorityBorderColor(medication.priority)}`}
                                        >
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3 flex-1">
                                              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center">
                                                <Pill className="w-4 h-4 text-white" />
                                              </div>
                                              <div className="flex-1">
                                                <h5 className="font-semibold text-gray-900 text-sm">{medication.medication}</h5>
                                                <div className="flex items-center gap-2 mt-1">
                                                  <p className="text-xs text-gray-600">{medication.dosage}</p>
                                                  <span className="text-gray-400">•</span>
                                                  <p className="text-xs text-gray-600">Qty: {medication.quantity}</p>
                                                  <span className="text-gray-400">•</span>
                                                  <p className="text-xs text-gray-500">By: {medication.prescribedBy}</p>
                                                </div>
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-3 ml-4">
                                              <div className="text-right">
                                                <p className="text-sm font-bold text-gray-900">
                                                  {formatCurrency(medication.status === 'Cancelled' ? 0 : medication.totalCost)}
                                                </p>
                                                <div className="flex items-center gap-2 mt-1">
                                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(medication.status)}`}>
                                                    {medication.status}
                                                  </span>
                                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(medication.priority)}`}>
                                                    {medication.priority}
                                                  </span>
                                                </div>
                                              </div>
                                              {medication.status === 'Processing' && (
                                                <div className="flex flex-col items-end">
                                                  <div className="text-xs font-medium text-blue-600 mb-1">Processing...</div>
                                                  <div className="w-24 bg-gray-200 rounded-full h-1.5">
                                                    <div className="bg-blue-600 h-1.5 rounded-full" style={{width: '60%'}}></div>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Compliance Tab */}
        {activeTab === 'compliance' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold mb-2">Compliance Monitoring</h2>
              <p className="opacity-90">Track medication compliance and regulatory adherence</p>
            </div>

            {/* Compliance Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200 shadow-lg h-[140px] flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-600 text-sm font-medium">Compliance Rate</p>
                    <p className="text-3xl font-bold text-green-900">94.2%</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-6 border border-yellow-200 shadow-lg h-[140px] flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-yellow-600 text-sm font-medium">PBS Compliance</p>
                    <p className="text-3xl font-bold text-yellow-900">98.7%</p>
                  </div>
                  <Shield className="w-8 h-8 text-yellow-600" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200 shadow-lg h-[140px] flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-600 text-sm font-medium">Authority Required</p>
                    <p className="text-3xl font-bold text-blue-900">
                      {ordersData.filter(o => o.authority).length}
                              </p>
                            </div>
                  <FileText className="w-8 h-8 text-blue-600" />
                          </div>
              </div>
            </div>

            {/* Compliance Issues */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Compliance Issues</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <div>
                          <h4 className="font-semibold text-red-800">Missing Authority Numbers</h4>
                          <p className="text-sm text-red-600">3 medications require authority approval</p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-red-200 text-red-800 text-xs font-medium rounded-full">
                        HIGH PRIORITY
                              </span>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Clock className="w-5 h-5 text-yellow-600" />
                        <div>
                          <h4 className="font-semibold text-yellow-800">PBS Eligibility Review</h4>
                          <p className="text-sm text-yellow-600">5 medications need PBS eligibility verification</p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-yellow-200 text-yellow-800 text-xs font-medium rounded-full">
                        MEDIUM
                              </span>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <div>
                          <h4 className="font-semibold text-blue-800">Documentation Update</h4>
                          <p className="text-sm text-blue-600">2 orders need updated documentation</p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-blue-200 text-blue-800 text-xs font-medium rounded-full">
                        LOW
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold mb-2">Order Reports & Analytics</h2>
              <p className="opacity-90">Comprehensive reporting and analytics dashboard</p>
                          </div>

            {/* Report Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                        </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Order Summary</h3>
                    <p className="text-sm text-gray-600">Monthly order statistics</p>
                    </div>
                </div>
                <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  Generate Report
                </button>
                  </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Cost Analysis</h3>
                    <p className="text-sm text-gray-600">Medication cost trends</p>
                </div>
                </div>
                <button className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                  Generate Report
                </button>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Shield className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Compliance Report</h3>
                    <p className="text-sm text-gray-600">Regulatory compliance status</p>
                  </div>
                </div>
                <button className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                  Generate Report
                </button>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Patient Analysis</h3>
                    <p className="text-sm text-gray-600">Patient medication patterns</p>
                  </div>
                </div>
                <button className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors">
                  Generate Report
                </button>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Alert Summary</h3>
                    <p className="text-sm text-gray-600">System alerts and issues</p>
                  </div>
                </div>
                <button className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                  Generate Report
                </button>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <Download className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Export Data</h3>
                    <p className="text-sm text-gray-600">Export all order data</p>
                  </div>
                </div>
                <button className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
                  Export Data
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Quick Statistics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">{ordersData.length}</div>
                  <div className="text-sm text-gray-600">Total Orders</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {formatCurrency(ordersData.reduce((sum, order) => sum + (order.status === 'Cancelled' ? 0 : order.totalCost), 0))}
                  </div>
                  <div className="text-sm text-gray-600">Total Value</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">
                    {ordersData.filter(o => o.pbsEligible).length}
                  </div>
                  <div className="text-sm text-gray-600">PBS Eligible</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600 mb-2">
                    {ordersData.filter(o => o.authority).length}
                  </div>
                  <div className="text-sm text-gray-600">Authority Required</div>
                </div>
              </div>
            </div>
            </div>
          )}
      </div>

      {/* Bulk Order Modal */}
      {showBulkOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Bulk Order Management</h2>
                    <p className="text-blue-100">Step {bulkOrderStep} of 3: {bulkOrderStep === 1 ? 'Select Patients' : bulkOrderStep === 2 ? 'Review Medications' : 'Group Orders'}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowBulkOrderModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {bulkOrderStep === 1 && (
            <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-blue-800 mb-2">Select Patients for Bulk Order</h3>
                    </div>

                  {/* Patient Selection */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Available Patients ({patientMedicationData.length})</h3>
                      <button
                        onClick={selectAllPatients}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        Select All
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                      {patientMedicationData.map((patient) => (
                        <div
                          key={patient.id}
                          onClick={() => togglePatientSelection(patient)}
                          className={`p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 ${
                            selectedPatients.some(p => p.id === patient.id)
                              ? 'border-blue-500 bg-blue-50 shadow-lg'
                              : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-4 h-4 rounded border-2 ${
                              selectedPatients.some(p => p.id === patient.id)
                                ? 'bg-blue-500 border-blue-500'
                                : 'border-gray-300'
                            }`}>
                              {selectedPatients.some(p => p.id === patient.id) && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{patient.name}</h4>
                              <p className="text-sm text-gray-600">{getAccommodationTerm(patient.accommodation || patient.ward)} • {patient.medications.length} medications</p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                  {/* Selected Patients Summary */}
                  {selectedPatients.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <h3 className="text-lg font-semibold text-green-800 mb-2">Selected Patients ({selectedPatients.length})</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedPatients.map((patient) => (
                          <span key={patient.id} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                            {patient.name}
                          </span>
                        ))}
                    </div>
                    </div>
                  )}
                  </div>
              )}

              {bulkOrderStep === 2 && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-blue-800 mb-2">Review Medications</h3>
                    <p className="text-blue-600">Review and select medications for the selected patients</p>
                </div>

                  {/* Medication Selection */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold mb-4">Available Medications</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                      {availableMedications.map((medication) => (
                        <div
                          key={medication.name}
                          onClick={() => toggleMedicationSelection(medication)}
                          className={`p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 ${
                            selectedMedications.some(m => m.name === medication.name)
                              ? 'border-blue-500 bg-blue-50 shadow-lg'
                              : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-4 h-4 rounded border-2 ${
                              selectedMedications.some(m => m.name === medication.name)
                                ? 'bg-blue-500 border-blue-500'
                                : 'border-gray-300'
                            }`}>
                              {selectedMedications.some(m => m.name === medication.name) && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                    </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{medication.name}</h4>
                              <p className="text-sm text-gray-600">{formatCurrency(medication.unitPrice)} per unit</p>
                              <p className="text-xs text-green-600">{medication.pbsEligible ? 'PBS Eligible' : 'Private'}</p>
                    </div>
                  </div>
                        </div>
                      ))}
                </div>
              </div>
            </div>
          )}

              {bulkOrderStep === 3 && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-blue-800 mb-2">Group Orders</h3>
                    <p className="text-blue-600">Review and confirm the bulk order grouping</p>
                  </div>

                  {/* Order Summary */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{selectedPatients.length}</div>
                        <div className="text-sm text-blue-600">Patients</div>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{selectedMedications.length}</div>
                        <div className="text-sm text-green-600">Medications</div>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">
                          {formatCurrency(selectedMedications.reduce((sum, med) => sum + med.unitPrice, 0))}
                        </div>
                        <div className="text-sm text-purple-600">Estimated Total</div>
                      </div>
                    </div>
                  </div>
            </div>
          )}
        </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-6">
              <div className="flex justify-between">
                <button
                  onClick={() => setBulkOrderStep(Math.max(1, bulkOrderStep - 1))}
                  disabled={bulkOrderStep === 1}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowBulkOrderModal(false)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  {bulkOrderStep < 3 ? (
                    <button
                      onClick={() => setBulkOrderStep(bulkOrderStep + 1)}
                      disabled={selectedPatients.length === 0}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        showToast('Bulk order submitted successfully!', 'success');
                        setShowBulkOrderModal(false);
                        setBulkOrderStep(1);
                        setSelectedPatients([]);
                        setSelectedMedications([]);
                      }}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Submit Order
                    </button>
                  )}
      </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
